import { MACRO_DELAY_MINUTES } from 'dashboard/routes/dashboard/settings/automation/constants';

const allElementsString = arr => {
  return arr.every(elem => typeof elem === 'string');
};

const allElementsNumbers = arr => {
  return arr.every(elem => typeof elem === 'number');
};

const formatArray = params => {
  if (params.length <= 0) {
    params = [];
  } else if (allElementsString(params) || allElementsNumbers(params)) {
    params = [...params];
  } else {
    params = params.map(val => val?.id || val);
  }
  return params;
};

// add_board must stay [{ board_id, status_id }]. Generic formatArray turns [{ id }] into [id], which breaks edit reload.
const buildAddBoardActionParams = item => {
  const params = item.action_params;
  const statusFromForm = item?.status?.id;
  if (!Array.isArray(params) || params.length === 0) {
    return [];
  }
  const first = params[0];
  if (first && typeof first === 'object' && first.board_id != null) {
    return [
      {
        board_id: first.board_id,
        status_id: first.status_id ?? statusFromForm,
      },
    ];
  }
  if (first && typeof first === 'object' && first.id != null) {
    return [{ board_id: first.id, status_id: statusFromForm }];
  }
  if (typeof first === 'number' || typeof first === 'string') {
    return [{ board_id: first, status_id: statusFromForm }];
  }
  return [];
};

const buildSendWhatsappTemplateActionParams = item => {
  const config = item.action_params || {};
  const delayMinutes = Number(item.delay_minutes);
  const payload = {
    inbox_id: config.inbox_id ?? null,
    macro_id: config.macro_id ?? null,
  };

  if (Number.isFinite(delayMinutes) && delayMinutes > 0) {
    payload.delay_minutes = Math.min(
      Math.floor(delayMinutes),
      MACRO_DELAY_MINUTES.MAX
    );
  }

  return [payload];
};

const buildAddMacroActionParams = item => {
  const macroIds = formatArray(item.action_params || []);
  const delayMinutes = Number(item.delay_minutes);

  if (!Number.isFinite(delayMinutes) || delayMinutes <= 0) {
    return macroIds;
  }

  return [
    {
      macro_ids: macroIds,
      delay_minutes: Math.min(
        Math.floor(delayMinutes),
        MACRO_DELAY_MINUTES.MAX
      ),
    },
  ];
};

const buildSendEmailToTeamActionParams = params => {
  const source = Array.isArray(params) ? params[0] : params;
  if (!source || typeof source !== 'object') {
    return [];
  }
  const teamIds = (source.team_ids || [])
    .map(team => (team && typeof team === 'object' ? team.id : team))
    .filter(id => id !== null && id !== undefined && id !== '');
  return [{ team_ids: teamIds, message: source.message ?? '' }];
};

const generatePayloadForObject = (item, status=null) => {
  if (item.action_name === 'email_template') {
    const templateId = item.action_params.id;
    const subjectText = item.action_params.subject ?? '';
    const bodyText = item.action_params.body ?? '';
    item.action_params = [templateId, subjectText, bodyText];
    return item.action_params;
  }
  if (item.payload === "whatsapp_template" || item.action_name === "whatsapp_template") {
    const templateId = item.action_params.id;
    const templateText = item.action_params.components?.[0]?.text || '';
    const mediaUrl = item.action_params.components?.[0]?.header_handle || 
                     item.action_params.components?.find(c => c.type === 'HEADER')?.example?.header_handle ||
                     item.action_params.header_handle ||
                     null;
    // Get the media format (IMAGE, VIDEO, DOCUMENT)
    const mediaFormat = item.action_params.components?.[0]?.format ||
                        item.action_params.components?.find(c => c.type === 'HEADER')?.format ||
                        null;
    // Preserve mediaUrl as third element and mediaFormat as fourth element if they exist
    if (mediaUrl) {
      item.action_params = [templateId, templateText, mediaUrl, mediaFormat];
    } else {
      item.action_params = [templateId, templateText];
    }
  }
  else if(item.action_name == "add_board"){
    const boardId = item.action_params.id;

    item.action_params = [
      { board_id: boardId,
        status_id: status?.id 
      },
    ];
  }
  else if (item.action_name === "send_api_webhook") {
    // For api_webhook, preserve the object structure as an array with single object
    item.action_params = [item.action_params];
  }
  else if (item.action_name === "set_reminder") {
    // For set_reminder, preserve the object structure as an array with single object
    item.action_params = [item.action_params];
  }
  else if (item.action_name === "send_ai_generated_email") {
    // Keep { inbox_id, macro_id } wrapped in an array to match backend expectation.
    item.action_params = [item.action_params];
  }
  else if (item.action_name === "send_whatsapp_template") {
    item.action_params = buildSendWhatsappTemplateActionParams(item);
    delete item.delay_minutes;
  }
  else if (item.action_name === "send_kivo_notification") {
    item.action_params = [item.action_params];
  }
  else if (item.action_name === "send_kivo_notification_on_no_engagement") {
    item.action_params = [item.action_params];
  }
  else if (item.action_name === 'send_email_to_team') {
    item.action_params = buildSendEmailToTeamActionParams(item.action_params);
  }
  else if (item.action_params.id) {
    item.action_params = [item.action_params.id];
  } else {
    item.action_params = [item.action_params];
  }
  return item.action_params;
};

const generatePayload = data => {
  const actions = JSON.parse(JSON.stringify(data));
  let payload = actions.map(item => {
    if (Array.isArray(item.action_params)) {
      // Special handling for whatsapp_template to preserve third element (media URLs)
      if (item.action_name === 'whatsapp_template' || item.payload === 'whatsapp_template') {
        // Preserve the array structure: [id, text, mediaUrl]
        item.action_params = item.action_params.map(val => val?.id || val);
      } else if (item.action_name === 'email_template') {
        item.action_params = item.action_params.map(val => val?.id || val);
      } else if (item.action_name === 'add_board') {
        item.action_params = buildAddBoardActionParams(item);
      } else if (item.action_name === 'send_email_to_team') {
        item.action_params = buildSendEmailToTeamActionParams(item.action_params);
      } else if (item.action_name === 'add_macro') {
        item.action_params = buildAddMacroActionParams(item);
        delete item.delay_minutes;
      } else if (item.action_name === 'send_whatsapp_template') {
        item.action_params = buildSendWhatsappTemplateActionParams(item);
        delete item.delay_minutes;
      } else {
        item.action_params = formatArray(item.action_params);
      }
    } else if (typeof item.action_params === 'object') {
      item.action_params = generatePayloadForObject(item, item?.status);
    } else if (!item.action_params) {
      item.action_params = [];
    } else {
      item.action_params = [item.action_params];
    }
    return item;
  });
  return payload;
};

export default generatePayload;
